import React, {useContext} from 'react';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Snackbar from '@material-ui/core/Snackbar';
import Alert from '@material-ui/lab/Alert';
import { useTranslation } from "react-i18next";
import SecureFetchContext from 'context/SecureFetchContext';
import {
    Tooltip, makeStyles, Box, CircularProgress
} from '@material-ui/core';
import getApiRoute from 'routes/apiRoutes';
import {EventAvailable} from "@material-ui/icons";
import {getDefaultAddress, getShipperLogoPath} from "../../../helpers";
import AppContext from "../../../context/AppContext";
import PackagePrintProtocolFromDate from "./PackagePrintProtocolFromDate";
import { SHIPPER_SHORTCUTS } from "constants/shippers";

const useStyles = makeStyles((theme) => ({
    root: {},
    button: {
        marginRight: "10px"
    },
    shippersGrid: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'center',
        alignItems: 'center',
        alignContent: 'center',
        width: '100%',
        gap: `${theme.spacing(2)}px`,
    },
    boxItem: {
        flex: '0 0 150px',
        maxWidth: '150px',
    }
}));

export default function DailyClosingDialog({ setErrors, loadPackages, ...rest }) {
    const classes = useStyles();
    const { t } = useTranslation();
    const { appValues } = useContext(AppContext);
    const secureFetch = useContext(SecureFetchContext);
    const [open, setOpen] = React.useState(false);
    const [selectedShipper, setSelectedShipper] = React.useState(null);
    const [spsProtocol, setSpsProtocol] = React.useState(null);
    const [isLoading, setIsLoading] = React.useState(false);
    const [successMessage, setSuccessMessage] = React.useState(null);

    const [date, setDate] = React.useState((new Date()).toISOString().slice(0, 10));
    const [pickup, setPickup] = React.useState(getDefaultAddress(appValues.user));

    const spsEnabled = appValues.user.user_shipper_price_lists.find(v => v.shipper.shortcut === 'SPS');
    const skpostEnabled = appValues.user.user_shipper_price_lists.find(v => v.shipper.shortcut === SHIPPER_SHORTCUTS.SKPOST);
    const packetaEnabled = appValues.user.user_shipper_price_lists.find(v => v.shipper.shortcut === 'Packeta');
    const QDLEnabled = appValues.user.user_shipper_price_lists.find(v => v.shipper.shortcut === 'QDL');

    const dailyClosing = () => {
        let values = {
            action: 'daily_closing',
        }
        setIsLoading(true);
        secureFetch(getApiRoute('package_bulk'), values, 'POST', true, true, true, true).then(json => {
            if (json.protocol) {
                let a = document.createElement('a');
                a.href = 'data:application/pdf;base64,' + json.protocol;
                a.download = "protocol.pdf";
                document.body.appendChild(a); // we need to append the element to the dom -> otherwise it will not work in firefox
                a.click();
                a.remove();
                setSpsProtocol(json.protocol);
            }
            if (json.errors !== '') {
                setErrors(json.errors);
            }
        }).finally(() => setIsLoading(false));
    };

    const ACTIONS_MAPPER = {
        'QDL': 'QDL_acceptance_protocol',
        'PACKETA': 'packeta_acceptance_protocol',
        [SHIPPER_SHORTCUTS.SKPOST]: SHIPPER_SHORTCUTS.SKPOST + '_daily_closing',
    }

    const handleSubmit = (event) => {
        event.preventDefault();
        const action = ACTIONS_MAPPER[selectedShipper];
        if (!action) {
            return;
        }
        const payload = { action };
        if (selectedShipper === 'PACKETA' || selectedShipper === 'QDL') {
            payload.pickup = pickup.id;
            payload.date = date;
        }
        setIsLoading(true);
        secureFetch(getApiRoute('package_bulk'), payload, 'POST', true, true, true, true).then(json => {
            handleClose();
            if (json.protocol) {
                let a = document.createElement('a');
                a.href = 'data:application/pdf;base64,' + json.protocol;
                a.download = "protocol.pdf";
                document.body.appendChild(a);
                a.click();
                a.remove();
                loadPackages();
            } else {
                if (json.errors && json.errors !== '') {
                    setErrors(json.errors);
                } else if (selectedShipper === SHIPPER_SHORTCUTS.SKPOST) {
                    setSuccessMessage(t('messages.daily_closing_success'));
                }
            }
        }).finally(() => setIsLoading(false));
    };

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedShipper(null);
    };

    const handleShipperChange = (shipper) => {
        setSelectedShipper(shipper);
    }

    return (
        <React.Fragment>
            <Tooltip title={t('button.daily_closing')} aria-label={t('button.daily_closing')}>
                <Button
                    variant="contained"
                    color="secondary"
                    style={{ backgroundColor: '#5f3f25'}}
                    size="small"
                    startIcon={<EventAvailable />}
                    aria-label={t('button.daily_closing')}
                    onClick={handleClickOpen}
                    className={classes.button}
                    disabled={isLoading}
                >
                    {t('button.daily_closing')}
                </Button>
            </Tooltip>
            <Dialog open={open} onClose={handleClose} aria-labelledby="form-dialog-title" disableEnforceFocus >
                <DialogTitle>{t('button.daily_closing')}</DialogTitle>
                <DialogContent>
                    {isLoading && (
                        <Box display="flex" justifyContent="center" mb={2}>
                            <CircularProgress size={24} />
                        </Box>
                    )}
                    { !selectedShipper &&
                        <React.Fragment>
                            <DialogContentText >
                                {t('messages.daily_closing_select_shipper')}
                            </DialogContentText>
                            <div style={{width: '100%'}}>
                                <Box className={classes.shippersGrid}>
                                    {
                                        spsEnabled &&
                                        <Box className={classes.boxItem}>
                                            <Button
                                                fullWidth
                                                href={spsProtocol ? 'data:application/pdf;base64,' + spsProtocol : null}
                                                onClick={spsProtocol ? null : dailyClosing}
                                                download={!!spsProtocol}
                                                disabled={isLoading}
                                            >
                                                <img width='100%' src={getShipperLogoPath('SPS')} alt={'SPS'}/>
                                            </Button>
                                        </Box>
                                    }
                                    {
                                        skpostEnabled &&
                                        <Box className={classes.boxItem}>
                                            <Button fullWidth onClick={() => handleShipperChange(SHIPPER_SHORTCUTS.SKPOST)} disabled={isLoading}>
                                                <img width='100%' src={getShipperLogoPath(SHIPPER_SHORTCUTS.SKPOST)} alt={SHIPPER_SHORTCUTS.SKPOST}/>
                                            </Button>
                                        </Box>
                                    }
                                    {
                                        packetaEnabled &&
                                        <Box className={classes.boxItem}>
                                            <Button fullWidth onClick={() => handleShipperChange('PACKETA')} disabled={isLoading}>
                                                <img width='100%' src={getShipperLogoPath('PACKETA')} alt={'Packeta'}/>
                                            </Button>
                                        </Box>
                                    }
                                    {
                                        QDLEnabled &&
                                        <Box className={classes.boxItem}>
                                            <Button fullWidth onClick={() => handleShipperChange('QDL')} disabled={isLoading}>
                                                <img width='100%' style={{maxWidth: '100%'}} src={getShipperLogoPath('QDL')} alt={'QDL'}/>
                                            </Button>
                                        </Box>
                                    }
                                </Box>
                            </div>
                        </React.Fragment>
                    }
                    {
                        selectedShipper === SHIPPER_SHORTCUTS.SKPOST &&
                        <DialogContentText>
                            {t('messages.skpost_daily_closing_info')}
                        </DialogContentText>
                    }
                    {
                        selectedShipper === 'PACKETA' &&
                        <PackagePrintProtocolFromDate
                            buttonTitle={t('button.packeta_acceptance_protocol')}
                            dialogTitle={t('tooltip.print_packeta_protocol')}
                            setErrors={setErrors}
                            loadPackages={loadPackages}
                            handleClose={handleClose}
                            date={date}
                            setDate={setDate}
                            selectedShipper={selectedShipper}
                            pickup={pickup}
                            setPickup={setPickup}
                        />
                    }
                    {
                        selectedShipper === 'QDL' &&
                        <PackagePrintProtocolFromDate
                            buttonTitle={t('button.QDL_acceptance_protocol')}
                            dialogTitle={t('tooltip.print_QDL_protocol')}
                            setErrors={setErrors}
                            loadPackages={loadPackages}
                            handleClose={handleClose}
                            date={date}
                            setDate={setDate}
                            selectedShipper={selectedShipper}
                            pickup={pickup}
                            setPickup={setPickup}
                        />
                    }
                </DialogContent>
                <DialogActions>
                    {selectedShipper &&
                    <Button color="primary" variant="text" onClick={() => handleShipperChange(null)} disabled={isLoading} >
                        {t('button.back')}
                    </Button>
                    }
                    <Button color="secondary" variant="contained" onClick={handleClose} disabled={isLoading} >
                        {t('button.close')}
                    </Button>
                    {selectedShipper &&
                    <Button color="primary" variant="contained" onClick={handleSubmit} disabled={isLoading} >
                        {t('button.process')}
                    </Button>
                    }
                </DialogActions>
            </Dialog>
            <Snackbar open={successMessage !== null} autoHideDuration={1500} onClose={() => setSuccessMessage(null)}>
                <Alert onClose={() => setSuccessMessage(null)} variant="filled" severity="success">
                    {successMessage}
                </Alert>
            </Snackbar>
        </React.Fragment>
    );
}
